import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AgentLayout } from "@/components/AgentLayout";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  UsersThree, MagnifyingGlass, Envelope, Phone, Suitcase, CircleNotch,
  ArrowSquareOut, User, PencilSimple, Trash, DownloadSimple, WarningCircle, CheckCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatDateShort } from "@/lib/dateUtils";

const STATUS_STYLES = {
  Lead: "bg-white/5 text-white/60 border-white/10",
  Draft: "bg-white/5 text-white/60 border-white/10",
  Sent: "bg-blue-500/10 text-blue-300 border-blue-400/30",
  Approved: "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  Declined: "bg-amber-500/10 text-amber-300 border-amber-400/40",
  "Changes Requested": "bg-amber-500/10 text-amber-300 border-amber-400/40",
  Revised: "bg-blue-500/10 text-blue-300 border-blue-400/30",
  "Payment Link Sent": "bg-purple-500/10 text-purple-300 border-purple-400/40",
  "Payment Authorization Received": "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/40",
  "Deposit Confirmed": "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  "Deposit Paid": "bg-emerald-500/10 text-emerald-300 border-emerald-400/30",
  "Paid in Full": "bg-emerald-500/20 text-emerald-200 border-emerald-400/50",
  "Payment Received": "bg-emerald-500/10 text-emerald-300 border-emerald-400/30",
  Booked: "bg-green-500/15 text-green-300 border-green-400/40",
};

const ALL_STATUSES = [
  "Lead", "Draft", "Sent", "Approved", "Declined", "Changes Requested", "Revised",
  "Payment Link Sent", "Payment Authorization Received", "Deposit Confirmed",
  "Deposit Paid", "Paid in Full", "Payment Received", "Booked",
];

function StatusBadge({ status }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.Lead;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest border font-sans ${cls}`} data-testid={`client-status-${status}`}>
      {status}
    </span>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  return formatDateShort(iso) || iso;
}

export default function Clients() {
  const { user } = useAuth();
  const [clients, setClients] = useState(null);
  const [agents, setAgents] = useState([]); // for admin assign-agent dropdown
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  // Admin edit dialog
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const isAdmin = user?.role === "admin";

  const load = async () => {
    try {
      const { data } = await api.get("/clients");
      setClients(data);
    } catch {
      setClients([]);
    }
  };

  useEffect(() => {
    load();
    if (isAdmin) {
      api.get("/admin/users").then(({ data }) => setAgents(data || [])).catch(() => {});
    }
  }, [isAdmin]);

  const filtered = useMemo(() => {
    if (!clients) return [];
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (statusFilter !== "All" && c.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (c.name || "").toLowerCase().includes(q) ||
        (c.email || "").toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q) ||
        (c.agent_name || "").toLowerCase().includes(q) ||
        (c.last_itinerary_title || "").toLowerCase().includes(q)
      );
    });
  }, [clients, search, statusFilter]);

  const statusOptions = useMemo(() => {
    const set = new Set(["All"]);
    (clients || []).forEach((c) => c.status && set.add(c.status));
    return Array.from(set);
  }, [clients]);

  const openEdit = (c) => {
    setEditing(c);
    setDraft({
      name: c.name || "",
      email: c.email || "",
      phone: c.phone || "",
      notes: c.notes || "",
      status: c.status || "Lead",
      agent_id: c.agent_id || "",
    });
  };

  const closeEdit = () => {
    setEditing(null);
    setDraft({});
    setSaving(false);
  };

  const saveEdit = async (e) => {
    e?.preventDefault?.();
    if (!editing) return;
    setSaving(true);
    try {
      const payload = {};
      ["name", "email", "phone", "notes", "status", "agent_id"].forEach((k) => {
        if (draft[k] !== editing[k] && (draft[k] != null && draft[k] !== "")) payload[k] = draft[k];
        // Allow clearing notes to empty string explicitly
        if (k === "notes" && draft[k] !== editing[k]) payload[k] = draft[k] || "";
      });
      if (Object.keys(payload).length === 0) {
        toast.info("No changes to save");
        setSaving(false);
        return;
      }
      await api.put(`/admin/clients/${editing.id}`, payload);
      toast.success("Client updated");
      await load();
      closeEdit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save changes");
      setSaving(false);
    }
  };

  const requestDelete = () => {
    if (!editing) return;
    setConfirmDelete(editing);
  };

  const performDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/clients/${confirmDelete.id}`);
      toast.success("Client deleted");
      setConfirmDelete(null);
      closeEdit();
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Server returns the spec-mandated copy verbatim — surface it as-is.
      toast.error(detail || "Could not delete client");
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const exportCsv = () => {
    if (!clients?.length) {
      toast.error("Nothing to export");
      return;
    }
    const rows = filtered.length > 0 ? filtered : clients;
    const esc = (v) => {
      const s = v == null ? "" : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const headers = ["Full Name", "Email", "Phone Number", "Agent Name", "Trip Name", "Booking Status", "Notes", "Date Added"];
    const lines = [headers.join(",")];
    rows.forEach((c) => {
      lines.push([
        esc(c.name || ""),
        esc(c.email || ""),
        esc(c.phone || ""),
        esc(c.agent_name || ""),
        esc(c.last_itinerary_title || ""),
        esc(c.status || "Lead"),
        esc(c.notes || ""),
        esc(c.created_at ? new Date(c.created_at).toISOString().slice(0, 10) : ""),
      ].join(","));
    });
    const csv = "\uFEFF" + lines.join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `clients-${date}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${rows.length} client${rows.length === 1 ? "" : "s"}`);
  };

  const colSpan = isAdmin ? "md:col-span-1" : "md:col-span-2"; // status col

  return (
    <AgentLayout>
      <div className="mb-8 animate-fade-up">
        <p className="text-[11px] uppercase tracking-[0.35em] text-[#D4AF37] mb-3 font-sans">Contacts</p>
        <h1 className="font-serif text-5xl sm:text-6xl tracking-tight text-white mb-3">
          Your <span className="italic text-[#D4AF37]">clients</span>
        </h1>
        <p className="text-white/60 text-sm max-w-xl font-sans">
          {isAdmin
            ? "Every traveler in the Best Time Travel & Tours ecosystem — across all agents."
            : "The travelers you've curated journeys for. Contact them any time."}
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" weight="duotone" />
          <Input
            placeholder="Search by name, email, phone, or trip…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-11 h-12 bg-ink-surface border-white/10 text-white focus:border-[#D4AF37] focus:ring-[#D4AF37]/20 font-sans"
            data-testid="clients-search-input"
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {statusOptions.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`shrink-0 h-10 px-4 rounded-full text-[11px] uppercase tracking-widest border font-sans transition-colors ${
                statusFilter === s
                  ? "bg-[#D4AF37] text-black border-[#D4AF37]"
                  : "bg-ink-surface text-white/60 border-white/10 hover:text-[#D4AF37] hover:border-[#D4AF37]/40"
              }`}
              data-testid={`clients-filter-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
        {isAdmin && (
          <Button
            onClick={exportCsv}
            className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-10 px-5 text-xs uppercase tracking-widest sm:shrink-0"
            data-testid="clients-export-btn"
          >
            <DownloadSimple size={15} weight="duotone" /> Export Clients (CSV)
          </Button>
        )}
      </div>

      {clients === null ? (
        <div className="py-20 flex justify-center"><CircleNotch size={28} className="text-[#D4AF37] animate-spin" /></div>
      ) : clients.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-ink-surface p-10 text-center" data-testid="clients-empty-state">
          <UsersThree size={36} className="mx-auto text-white/30 mb-3" weight="duotone" />
          <p className="font-serif text-2xl text-white mb-1">No clients yet</p>
          <p className="text-white/50 text-sm font-sans">Clients will appear here automatically when you create an itinerary with their email.</p>
        </div>
      ) : (
        <section className="rounded-lg border border-white/10 overflow-hidden" data-testid="clients-table">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-ink-surface text-[10px] uppercase tracking-widest text-white/50 border-b border-white/10 font-sans">
            <div className="col-span-3">Client</div>
            <div className="col-span-3">Contact</div>
            {isAdmin && <div className="col-span-2">Agent</div>}
            <div className={isAdmin ? "col-span-1" : "col-span-3"}>Trip</div>
            <div className={colSpan}>Status</div>
            <div className="col-span-1 text-right">{isAdmin ? "Actions" : "Added"}</div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-10 text-center text-white/50 text-sm font-sans">No clients match your filters.</div>
          ) : (
            filtered.map((c) => (
              <div
                key={c.id}
                className="border-b border-white/5 last:border-b-0 px-5 py-4 md:py-5 hover:bg-ink-surface transition-colors md:grid md:grid-cols-12 md:gap-4 md:items-center"
                data-testid={`client-row-${c.id}`}
              >
                {/* Client name */}
                <div className="md:col-span-3 flex items-center gap-3 min-w-0">
                  <div className="shrink-0 w-9 h-9 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center">
                    <User size={15} className="text-[#D4AF37]" weight="duotone" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-white font-sans truncate">{c.name || "—"}</p>
                    <p className="text-[10px] uppercase tracking-widest text-white/30 font-sans md:hidden">Client</p>
                  </div>
                </div>

                {/* Contact */}
                <div className="md:col-span-3 min-w-0 mt-3 md:mt-0 space-y-1">
                  <a href={`mailto:${c.email}`} className="flex items-center gap-2 text-xs text-white/70 hover:text-[#D4AF37] transition-colors font-sans truncate" data-testid={`client-email-${c.id}`}>
                    <Envelope size={13} weight="duotone" />
                    <span className="truncate">{c.email}</span>
                  </a>
                  {c.phone ? (
                    <a href={`tel:${c.phone}`} className="flex items-center gap-2 text-xs text-white/50 hover:text-[#D4AF37] transition-colors font-sans" data-testid={`client-phone-${c.id}`}>
                      <Phone size={13} weight="duotone" />
                      <span>{c.phone}</span>
                    </a>
                  ) : (
                    <span className="flex items-center gap-2 text-xs text-white/25 font-sans">
                      <Phone size={13} weight="duotone" />
                      <span>—</span>
                    </span>
                  )}
                </div>

                {/* Admin: agent column */}
                {isAdmin && (
                  <div className="md:col-span-2 min-w-0 mt-3 md:mt-0">
                    <p className="text-white/80 text-sm font-sans truncate">{c.agent_name || "—"}</p>
                    <p className="text-white/30 text-[11px] font-sans truncate">{c.agent_email}</p>
                  </div>
                )}

                {/* Trip */}
                <div className={`${isAdmin ? "md:col-span-1" : "md:col-span-3"} min-w-0 mt-3 md:mt-0`}>
                  {c.last_itinerary_id ? (
                    <Link
                      to={`/itineraries/${c.last_itinerary_id}`}
                      className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-[#D4AF37] transition-colors font-sans truncate max-w-full"
                      data-testid={`client-trip-link-${c.id}`}
                    >
                      <Suitcase size={13} weight="duotone" className="shrink-0" />
                      <span className="truncate">{c.last_itinerary_title || "—"}</span>
                      <ArrowSquareOut size={11} className="shrink-0 opacity-60" />
                    </Link>
                  ) : (
                    <span className="text-white/40 text-sm font-sans">—</span>
                  )}
                  {c.itineraries_count > 1 && (
                    <p className="text-[10px] text-white/40 font-sans mt-0.5">
                      +{c.itineraries_count - 1} more trip{c.itineraries_count > 2 ? "s" : ""}
                    </p>
                  )}
                </div>

                {/* Status */}
                <div className={`${colSpan} mt-3 md:mt-0`}>
                  <StatusBadge status={c.status || "Lead"} />
                </div>

                {/* Actions / Date */}
                <div className="md:col-span-1 text-left md:text-right mt-2 md:mt-0">
                  {isAdmin ? (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEdit(c)}
                      className="h-8 px-2 text-white/70 hover:text-[#D4AF37] hover:bg-[#D4AF37]/10 text-xs uppercase tracking-widest font-sans"
                      data-testid={`client-edit-${c.id}`}
                    >
                      <PencilSimple size={13} weight="duotone" className="mr-1.5" /> Edit
                    </Button>
                  ) : (
                    <span className="text-[11px] text-white/40 font-sans">{formatDate(c.created_at)}</span>
                  )}
                </div>
              </div>
            ))
          )}
        </section>
      )}

      <p className="text-[11px] text-white/30 font-sans italic mt-6">
        Clients are added automatically when you create an itinerary with their email address.
      </p>

      {/* ---- Admin Edit Dialog ---- */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && closeEdit()}>
        <DialogContent className="bg-ink-surface border-white/10 text-white max-w-lg" data-testid="admin-client-edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-3xl">
              Edit <span className="italic text-[#D4AF37]">{editing?.name || "client"}</span>
            </DialogTitle>
            <DialogDescription className="text-white/50 text-xs font-sans">
              Changes cascade to every linked itinerary so the contact stays in sync.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-2 max-h-[70vh] overflow-y-auto pr-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Full name</Label>
                <Input value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} required className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="client-edit-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Phone number</Label>
                <Input value={draft.phone || ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} placeholder="+1 555 123 4567" className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="client-edit-phone" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Email</Label>
              <Input type="email" value={draft.email || ""} onChange={(e) => setDraft({ ...draft, email: e.target.value })} required className="bg-black/40 border-white/10 focus:border-[#D4AF37]" data-testid="client-edit-email" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Booking status</Label>
                <Select value={draft.status || "Lead"} onValueChange={(v) => setDraft({ ...draft, status: v })}>
                  <SelectTrigger className="bg-black/40 border-white/10" data-testid="client-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-ink-surface border-white/10 text-white max-h-72">
                    {ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">Assigned agent</Label>
                <Select value={draft.agent_id || ""} onValueChange={(v) => setDraft({ ...draft, agent_id: v })}>
                  <SelectTrigger className="bg-black/40 border-white/10" data-testid="client-edit-agent"><SelectValue placeholder="Choose agent" /></SelectTrigger>
                  <SelectContent className="bg-ink-surface border-white/10 text-white max-h-72">
                    {agents.map((a) => <SelectItem key={a.id} value={a.id}>{a.name || a.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-white/60 mb-2 block">
                Internal notes <span className="text-white/30 normal-case tracking-normal">(never shown to client)</span>
              </Label>
              <Textarea
                rows={3}
                value={draft.notes || ""}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                placeholder="e.g. Anniversary couple — prefers quiet resorts."
                className="bg-black/40 border-white/10 focus:border-[#D4AF37]"
                data-testid="client-edit-notes"
              />
            </div>

            {editing?.itineraries_count > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-md border border-amber-400/20 bg-amber-500/5 text-amber-200">
                <WarningCircle size={16} weight="duotone" className="mt-0.5 shrink-0" />
                <p className="text-xs font-sans">
                  This client has <strong>{editing.itineraries_count}</strong> linked itinerar{editing.itineraries_count === 1 ? "y" : "ies"}.
                  Changes to name/email/phone/agent will cascade to those records automatically.
                </p>
              </div>
            )}

            <DialogFooter className="gap-2 pt-2 sm:flex-row-reverse sm:justify-between">
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={closeEdit} className="border-white/20 bg-transparent text-white/80 hover:bg-white/5 h-11" data-testid="client-edit-cancel-btn">Cancel</Button>
                <Button type="submit" disabled={saving} className="bg-[#D4AF37] hover:bg-[#F3C853] text-black h-11 px-6 text-xs uppercase tracking-widest" data-testid="client-edit-save-btn">
                  {saving ? <CircleNotch size={16} className="animate-spin" /> : "Save changes"}
                </Button>
              </div>
              <Button
                type="button"
                variant="ghost"
                onClick={requestDelete}
                className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-11 px-3 text-xs uppercase tracking-widest sm:mr-auto"
                data-testid="client-edit-delete-btn"
              >
                <Trash size={14} weight="duotone" className="mr-1.5" /> Delete client
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---- Delete confirmation ---- */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent className="bg-ink-surface border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl">Delete this client?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60 font-sans">
              {confirmDelete && (
                <>
                  <span className="block text-white mb-1">{confirmDelete.name} · {confirmDelete.email}</span>
                  Are you sure you want to delete this client? This action cannot be undone. Linked itineraries (if any) will remain untouched.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/5" data-testid="client-delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="client-delete-confirm-btn"
            >
              {deleting ? <CircleNotch size={16} className="animate-spin" /> : <><CheckCircle size={14} weight="duotone" className="mr-1.5" /> Delete client</>}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AgentLayout>
  );
}
